"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, Variants } from "framer-motion";
import Title from "@/core/common/Title";
import { IPackageRecord } from "@/app/package/interface/IPackageData.interface";
import RichText from "@/utils/richText";

interface HolidayPackagesProps {
  holidayPackagesData: IPackageRecord[];
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const HolidayPackages = ({ holidayPackagesData }: HolidayPackagesProps) => {
  return (
    <section className="px-4 md:px-10 lg:px-20 mb-16 relative">
      <Title
        label="Start Exploring"
        primaryText="Unforgettable"
        highlightText="Adventures"
      />

      <Link href={`/package`} className="flex justify-end mb-3">
        <button className="group flex items-center gap-2 underline transition-all hover:text-primary-500">
          <span className="font-semibold text-sm tracking-wider">
            View All Packages
          </span>
        </button>
      </Link>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {holidayPackagesData?.slice(0, 4).map((tour) => (
          <motion.div
            key={tour.id}
            variants={cardVariants}
            className="w-full max-w-sm mx-auto rounded-2xl overflow-hidden border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-xl bg-white flex flex-col h-full"
          >
            <div className="relative h-56 w-full overflow-hidden group">
              <Image
                src={tour.image || "/placeholder.png"}
                alt={tour.name}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-110"
              />

              <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-md text-white text-xs font-bold px-3 py-1 rounded-full z-10">
                {tour.duration} days
              </div>

              <Link
                href={`/package/${tour.slug}`}
                className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
              >
                <span className="bg-primary-500 text-white font-medium px-5 py-2 rounded-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  View Details
                </span>
              </Link>
            </div>

            <div className="p-5 flex flex-col flex-grow">
              <div className="flex-grow space-y-3">
                <Link href={`/package/${tour.slug}`}>
                  <h2 className="font-bold text-gray-900 text-lg line-clamp-2 hover:text-primary-600 transition-colors">
                    {tour.name}
                  </h2>
                </Link>

                <RichText
                  content={tour?.description || ""}
                  className="text-sm text-gray-600 line-clamp-2 leading-relaxed mt-4"
                />
              </div>

              <div className="mt-5 pt-4 border-t border-gray-50">
                <p className="text-gray-400 text-[10px] uppercase font-bold tracking-widest mb-1">
                  Starting from
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black text-secondary-500">
                    Rs. {Number(tour.current_price).toLocaleString()}
                  </span>
                  <span className="text-gray-500 text-xs">/ person</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default HolidayPackages;
